import { rect, aRect, px } from '../../../pixel/canvas';
import { SLOT } from '../slots';
import type { Part } from '../../types';

// Anchor = top of head (same as hair). Hats draw upward from a.y.

export const hats: Record<string, Part> = {
  hat_cap: {
    id: 'hat_cap', category: 'hat', name: 'Cap', requires: SLOT.HAT,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y - 6, 13, 7, p.primary);      // dome
      aRect(ctx, a.x + 4, a.y, 14, 2, p.dark);         // brim (forward)
      aRect(ctx, a.x, a.y - 2, 13, 2, p.accentDark);   // band
      aRect(ctx, a.x, a.y - 6, 11, 1, p.accentLight);  // shine
    },
  },
  hat_tophat: {
    id: 'hat_tophat', category: 'hat', name: 'Top Hat', requires: SLOT.HAT,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y - 11, 10, 11, p.dark);       // cylinder
      aRect(ctx, a.x, a.y - 1, 18, 2, p.dark);         // brim
      aRect(ctx, a.x, a.y - 2, 10, 2, p.accent);       // band
      aRect(ctx, a.x, a.y - 11, 8, 1, p.metalDark);    // top edge
    },
  },
  hat_crown: {
    id: 'hat_crown', category: 'hat', name: 'Crown', requires: SLOT.HAT,
    draw: (ctx, a, p) => {
      // Base band
      aRect(ctx, a.x, a.y - 3, 14, 3, p.accent);
      // Points
      rect(ctx, a.x - 5, a.y - 7, 3, 5, p.accent);
      aRect(ctx, a.x, a.y - 8, 3, 6, p.accent);
      rect(ctx, a.x + 2, a.y - 7, 3, 5, p.accent);
      // Gems
      px(ctx, a.x - 4, a.y - 7, p.glass);
      px(ctx, a.x - 1, a.y - 8, p.accentLight);
      px(ctx, a.x + 3, a.y - 7, p.glass);
      aRect(ctx, a.x, a.y - 3, 12, 1, p.accentLight);
    },
  },
  hat_beanie: {
    id: 'hat_beanie', category: 'hat', name: 'Beanie', requires: SLOT.HAT,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y - 9, 14, 10, p.primary);     // main body
      aRect(ctx, a.x, a.y - 10, 10, 2, p.primary);     // rounded top
      aRect(ctx, a.x, a.y - 2, 14, 2, p.accentDark);   // brim/cuff
      // Ribbing
      aRect(ctx, a.x - 4, a.y - 8, 2, 6, p.dark);
      aRect(ctx, a.x, a.y - 8, 2, 6, p.dark);
      aRect(ctx, a.x + 4, a.y - 8, 2, 6, p.dark);
    },
  },
  hat_bow: {
    id: 'hat_bow', category: 'hat', name: 'Hair Bow', requires: SLOT.HAT,
    draw: (ctx, a, p) => {
      // Bow loops
      rect(ctx, a.x - 7, a.y - 5, 5, 5, p.accent);    // left loop
      rect(ctx, a.x + 2, a.y - 5, 5, 5, p.accent);    // right loop
      // Center knot
      aRect(ctx, a.x, a.y - 4, 4, 3, p.accentDark);
      // Loop highlights
      px(ctx, a.x - 6, a.y - 4, p.accentLight);
      px(ctx, a.x + 3, a.y - 4, p.accentLight);
    },
  },
  hat_headband: {
    id: 'hat_headband', category: 'hat', name: 'Headband', requires: SLOT.HAT,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y + 1, 14, 3, p.accent);       // band across head
      aRect(ctx, a.x, a.y + 1, 12, 1, p.accentLight);  // shine
    },
  },
  hat_halo: {
    id: 'hat_halo', category: 'hat', name: 'Halo', requires: SLOT.HAT,
    draw: (ctx, a, p) => {
      // Floating ring
      aRect(ctx, a.x, a.y - 8, 14, 2, p.accent);
      aRect(ctx, a.x, a.y - 9, 10, 1, p.accent);
      aRect(ctx, a.x, a.y - 8, 12, 1, p.accentLight);  // shine on ring
      // Support post (invisible but needed for floating illusion)
      aRect(ctx, a.x + 4, a.y - 7, 1, 5, p.accentDark);
    },
  },
  hat_helmet: {
    id: 'hat_helmet', category: 'hat', name: 'Helmet', requires: SLOT.HAT,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y - 10, 14, 11, p.metalDark);  // dome
      aRect(ctx, a.x, a.y - 11, 10, 2, p.metalDark);   // top
      // Visor
      aRect(ctx, a.x, a.y - 3, 12, 4, p.accentDark);
      aRect(ctx, a.x, a.y - 3, 10, 1, p.accent);       // visor shine
      // Side vents
      rect(ctx, a.x - 7, a.y - 7, 1, 4, p.metal);
      rect(ctx, a.x + 6, a.y - 7, 1, 4, p.metal);
      aRect(ctx, a.x, a.y - 10, 12, 1, p.metal);       // top shine
    },
  },
  hat_cowboy: {
    id: 'hat_cowboy', category: 'hat', name: 'Cowboy', requires: SLOT.HAT,
    draw: (ctx, a, p) => {
      aRect(ctx, a.x, a.y - 9, 12, 9, p.primary);      // crown
      aRect(ctx, a.x, a.y - 10, 8, 2, p.primary);      // pinch top
      aRect(ctx, a.x, a.y - 1, 22, 2, p.dark);         // wide brim
      aRect(ctx, a.x, a.y - 3, 12, 2, p.accentDark);   // hat band
      px(ctx, a.x - 1, a.y - 3, p.accent);             // band buckle
    },
  },
  hat_beret: {
    id: 'hat_beret', category: 'hat', name: 'Beret', requires: SLOT.HAT,
    draw: (ctx, a, p) => {
      // Tilted round beret
      aRect(ctx, a.x + 2, a.y - 7, 14, 7, p.primary);  // main puff (shifted right)
      aRect(ctx, a.x + 2, a.y - 8, 10, 2, p.primary);  // top dome
      aRect(ctx, a.x, a.y - 1, 14, 2, p.dark);         // brim band
      px(ctx, a.x + 6, a.y - 8, p.accentLight);        // shine
    },
  },
};
